import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Pencil, Trash2, ChevronDown } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import {
  Button,
  Input,
  Checkbox,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Skeleton,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useServicePageDefacement,
  useServicePageDefacementSites,
  useUpdateServicePageDefacement,
  defaultPageDefacement,
  toRelativePath,
  TAMPER_CONTENT_TYPES,
  type PageDefacementConfig,
  type TamperCacheURL,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { CheckboxField } from '@/pages/services/waap/components/CheckboxField';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './PageDefacementForm.module.css';

/**
 * Edit one website's Page Defacement Mitigation rule (the site's `config.tamper`).
 * Account-level: the site is addressed by its portal site-config id. Route:
 * aiwaf/advanced/page-defacement/:siteId.
 */
export function PageDefacementForm() {
  const navigate = useNavigate();
  const { serviceId, siteId } = useParams<{ serviceId: string; siteId: string }>();
  const { show } = useToast();
  const base = `/services/waap/${serviceId}`;

  const { data: existing, isLoading: loadingExisting } = useServicePageDefacement(serviceId, siteId);
  const { data: sites = [] } = useServicePageDefacementSites(serviceId);
  const domain = useMemo(
    () => sites.find((s) => String(s.site_id) === siteId)?.domain ?? '',
    [sites, siteId],
  );

  const [form, setForm] = useState<PageDefacementConfig>(() => defaultPageDefacement());
  const set = <K extends keyof PageDefacementConfig>(key: K, value: PageDefacementConfig[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    if (existing) setForm(existing);
  }, [existing]);

  // URL-path draft row. `type` is multi-select (the vendor accepts several
  // content types per path).
  const emptyDraft = (): { path: string; isRegex: boolean; type: string[] } => ({
    path: '',
    isRegex: false,
    type: [TAMPER_CONTENT_TYPES[0]],
  });
  const [draft, setDraft] = useState(emptyDraft);
  const toggleType = (t: string) =>
    setDraft((d) => ({
      ...d,
      type: d.type.includes(t) ? d.type.filter((x) => x !== t) : [...d.type, t],
    }));
  const addURL = () => {
    // The vendor only accepts a relative path with a leading slash (absolute
    // URLs / slashless paths 500 the /site PUT), so normalize before adding.
    const path = toRelativePath(draft.path);
    if (!path || draft.type.length === 0) return;
    const entry: TamperCacheURL = { path, is_regex: draft.isRegex, type: draft.type };
    set('cache_urls', [...form.cache_urls, entry]);
    setDraft(emptyDraft());
  };
  const removeURL = (i: number) =>
    set('cache_urls', form.cache_urls.filter((_, idx) => idx !== i));
  // Edit: pull the row back into the draft inputs and drop it from the table; the
  // user tweaks it and clicks Add to re-insert.
  const editURL = (i: number) => {
    const r = form.cache_urls[i];
    setDraft({ path: r.path, isRegex: r.is_regex, type: r.type.length ? r.type : [TAMPER_CONTENT_TYPES[0]] });
    removeURL(i);
  };

  const update = useUpdateServicePageDefacement(serviceId ?? '', siteId ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'page_defacement', 'edit');
      show({ tone: 'success', title: 'Page defacement mitigation saved' });
      navigate(`${base}/aiwaf/advanced?tab=page-defacement`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });

  // Numeric fields are required (positive) only when their owning section is on.
  const numbersValid =
    !form.enabled ||
    (form.disk_space > 0 && (!form.auto_update || (form.expire_time > 0 && form.web_similarity > 0)));
  const canSubmit = numbersValid && !update.isPending;

  const submit = () => update.mutate(form);

  if (loadingExisting) {
    return (
      <Container size="xl">
        <Skeleton className={styles.skelForm} />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs
            trail={[
              { label: 'AI-WAF' },
              { label: 'Advanced Protection', slug: 'aiwaf/advanced' },
              { label: 'Page Defacement Mitigation' },
            ]}
          />
        }
        title={domain || 'Page Defacement Mitigation'}
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
          <Card flat>
            <CardHeader>
              <CardTitle>Page Defacement Mitigation</CardTitle>
            </CardHeader>
            <CardBody>
            <div className={styles.fieldGrid}>
              <CheckboxField
                checked={form.enabled}
                onChange={(v) => set('enabled', v)}
                label="Enable"
                description="Cache intact copies of pages so visitors always get a clean page even if the origin is tampered with."
              />

              {form.enabled && (
                <>
                  <Field
                    label="Maximum Cache Space (MB)"
                    required
                    error={form.disk_space > 0 ? undefined : 'Enter a value greater than 0'}
                  >
                    {(props) => (
                      <Input
                        {...props}
                        type="number"
                        min={1}
                        value={String(form.disk_space)}
                        className={styles.diskInput}
                        onChange={(e) => set('disk_space', Number(e.target.value))}
                      />
                    )}
                  </Field>

                  <CheckboxField
                    checked={form.auto_update}
                    onChange={(v) => set('auto_update', v)}
                    label="Automatically Update Cache"
                    description="When enabled, cached pages auto-update on expiry — but only when the new page's similarity is at or above the threshold (a much lower similarity may indicate defacement, so the cache is kept)."
                  />

                  {form.auto_update && (
                    <div className={styles.inlineRow}>
                      <Field label="Cache Expiry Duration (Minutes)" required>
                        {(props) => (
                          <Input
                            {...props}
                            type="number"
                            min={1}
                            value={String(form.expire_time)}
                            className={styles.numInput}
                            onChange={(e) => set('expire_time', Number(e.target.value))}
                          />
                        )}
                      </Field>
                      <Field label="Similarity Threshold" required>
                        {(props) => (
                          <Input
                            {...props}
                            type="number"
                            min={1}
                            max={100}
                            value={String(form.web_similarity)}
                            className={styles.numInput}
                            onChange={(e) => set('web_similarity', Number(e.target.value))}
                          />
                        )}
                      </Field>
                    </div>
                  )}

                  <div>
                    <div className={styles.subTitle}>URL Paths</div>
                    <div className={styles.subDesc}>
                      Add page URL paths to cache.
                    </div>
                    <div className={styles.condRow}>
                      <Field label="URL Path" required>
                        {(props) => (
                          <Input
                            {...props}
                            value={draft.path}
                            maxLength={512}
                            placeholder="/path/to/page"
                            className={styles.urlInput}
                            onChange={(e) => setDraft((d) => ({ ...d, path: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addURL();
                              }
                            }}
                          />
                        )}
                      </Field>
                      <Field label="Regex">
                        {() => (
                          <div className={styles.regexBox}>
                            <Checkbox
                              checked={draft.isRegex}
                              onCheckedChange={(v) => setDraft((d) => ({ ...d, isRegex: !!v }))}
                            />
                          </div>
                        )}
                      </Field>
                      <Field label="Type" required>
                        {() => (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="secondary" className={styles.typeSelect}>
                                <span className={styles.typeValue}>
                                  {draft.type.length === 0
                                    ? 'Select type'
                                    : draft.type.length === 1
                                      ? draft.type[0]
                                      : `${draft.type.length} types`}
                                </span>
                                <ChevronDown size={14} />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent align="start">
                              <div className={styles.typeList}>
                                {TAMPER_CONTENT_TYPES.map((t) => (
                                  <label key={t} className={styles.typeOption}>
                                    <Checkbox
                                      checked={draft.type.includes(t)}
                                      onCheckedChange={() => toggleType(t)}
                                    />
                                    <span>{t}</span>
                                  </label>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                      </Field>
                      <Button variant="secondary" startIcon={<Plus size={14} />} onClick={addURL}>
                        Add
                      </Button>
                    </div>

                    <URLPathTable rows={form.cache_urls} onEdit={editURL} onRemove={removeURL} />
                  </div>
                </>
              )}
            </div>
            </CardBody>
          </Card>

          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => navigate(`${base}/aiwaf/advanced?tab=page-defacement`)}>
              Cancel
            </Button>
            <Button disabled={!canSubmit} onClick={submit}>
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
    </Container>
  );
}

/** Read-only table of added cache URL paths (URL Path / Type / Regex / remove). */
function URLPathTable({
  rows,
  onEdit,
  onRemove,
}: {
  rows: TamperCacheURL[];
  onEdit: (i: number) => void;
  onRemove: (i: number) => void;
}) {
  if (rows.length === 0) return null;
  return (
    <div className={styles.table}>
      <div className={styles.tableHead}>
        <div className={styles.cell}>URL Path</div>
        <div className={styles.cell}>Type</div>
        <div className={styles.cell}>Regex</div>
        <div className={styles.cell}>Operation</div>
      </div>
      {rows.map((r, i) => (
        <div key={i} className={styles.tableRow}>
          <div className={styles.cell}>{r.path}</div>
          <div className={styles.cell}>{r.type.join(', ') || '—'}</div>
          <div className={styles.cell}>{r.is_regex ? 'true' : 'false'}</div>
          <div className={`${styles.cell} ${styles.cellActions}`}>
            <IconButton aria-label="Edit" variant="ghost" onClick={() => onEdit(i)}>
              <Pencil size={16} />
            </IconButton>
            <IconButton aria-label="Remove" variant="ghost" onClick={() => onRemove(i)}>
              <Trash2 size={16} />
            </IconButton>
          </div>
        </div>
      ))}
    </div>
  );
}
